/* ==========================================================================
   Admin Faxes Page
   Pharmacy-side view of submitted prescription faxes (issue #130)
   ========================================================================== */

import { Fragment, useState, useEffect, useCallback } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  RotateCw,
  ClipboardList,
  ExternalLink,
  FileDown,
  Loader2,
  Search,
  X,
} from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { useAdminAuth } from '@/hooks/useAdminAuth'
import { useToast } from '@/context/ToastContext'
import { ApiClientError } from '@/api/client'
import {
  listPrescriptions,
  listProviders,
  getPrescriptionPdfUrl,
  retryFax,
  updateTrackingNumber,
  type PrescriptionListItem,
  type PrescriptionListResponse,
} from '@/api/admin'
import type { ProviderResponse, ShipmentInfo } from '@/api/types'

/* --------------------------------------------------------------------------
   Constants
   -------------------------------------------------------------------------- */

const PAGE_SIZE = 20

const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  submitted: { label: 'Submitted', className: 'bg-blue-100 text-blue-700' },
  fax_sent: { label: 'Fax Sent', className: 'bg-yellow-100 text-yellow-700' },
  delivered: { label: 'Delivered', className: 'bg-green-100 text-green-700' },
  failed: { label: 'Failed', className: 'bg-red-100 text-red-700' },
  // Only reachable for pharmacies with shipment tracking enabled (PORTAL-146).
  // Present so the badge renders rather than falling through to undefined; the
  // status filter below deliberately does not offer it.
  shipped: { label: 'Shipped', className: 'bg-emerald-100 text-emerald-700' },
}

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'fax_sent', label: 'Fax Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'failed', label: 'Failed' },
] as const

// Public tracking pages, keyed by the carrier string the bot records
// (PORTAL-146) with case and punctuation stripped. Other carriers render the
// tracking number as plain text.
const CARRIER_TRACKING_URLS: Record<
  string,
  (trackingNumber: string) => string
> = {
  ups: (n) => `https://www.ups.com/track?tracknum=${n}`,
  fedex: (n) => `https://www.fedex.com/fedextrack/?trknbr=${n}`,
  usps: (n) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${n}`,
  dhl: (n) =>
    `https://www.dhl.com/us-en/home/tracking/tracking-express.html?submit=1&tracking-id=${n}`,
}

/* --------------------------------------------------------------------------
   Helpers
   -------------------------------------------------------------------------- */

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function getPatientDisplay(rx: PrescriptionListItem): {
  name: string
  detail: string | null
} {
  if (rx.patient_type === 'human') {
    const name =
      `${rx.patient_first_name || ''} ${rx.patient_last_name || ''}`.trim() ||
      'N/A'
    return { name, detail: null }
  }
  const animalName = rx.animal_name || 'N/A'
  const species = rx.animal_species ? ` (${rx.animal_species})` : ''
  const owner = rx.owner_name ? `Owner: ${rx.owner_name}` : null
  return { name: `${animalName}${species}`, detail: owner }
}

function getProviderDisplay(rx: PrescriptionListItem): string {
  if (!rx.provider) return '—'
  return rx.provider.name?.trim() || rx.provider.email
}

function canDownloadPdf(rx: PrescriptionListItem): boolean {
  // Outage faxes stay in `submitted` but still have a generated PDF, so allow
  // viewing them too — only `delivered`/`fax_sent` were shown before.
  // `shipped` is past `delivered`, so the PDF is always present by then.
  return (
    rx.has_pdf &&
    (rx.status === 'submitted' ||
      rx.status === 'fax_sent' ||
      rx.status === 'delivered' ||
      rx.status === 'shipped')
  )
}

function getTrackingUrl(shipment: ShipmentInfo): string | null {
  if (!shipment.carrier || !shipment.tracking_number) return null
  const carrierKey = shipment.carrier.toLowerCase().replace(/[^a-z]/g, '')
  const buildUrl = CARRIER_TRACKING_URLS[carrierKey]
  return buildUrl
    ? buildUrl(encodeURIComponent(shipment.tracking_number))
    : null
}

function canRetryFax(rx: PrescriptionListItem): boolean {
  // A retry needs the generated PDF to re-send. Anything not yet delivered is
  // eligible; the backend enforces the max-attempts limit and surfaces a 400.
  // `shipped` is past `delivered` — the package is already out the door, so
  // re-faxing it would be worse than a no-op.
  return rx.has_pdf && rx.status !== 'delivered' && rx.status !== 'shipped'
}

/* --------------------------------------------------------------------------
   Status Badge
   -------------------------------------------------------------------------- */

function StatusBadge({ status, title }: { status: string; title?: string }) {
  const style = STATUS_STYLES[status] || {
    label: status,
    className: 'bg-gray-100 text-gray-700',
  }

  return (
    <span
      title={title}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style.className}`}
    >
      {style.label}
    </span>
  )
}

/**
 * Status badge plus a warning when the latest fax attempt failed but the
 * prescription hasn't exhausted its retries yet (overall status still
 * `fax_sent`/`submitted`) — otherwise the admin has no way to see that a
 * retry is needed. The fax error is surfaced as a hover tooltip.
 */
function FaxStatusCell({
  rx,
  align = 'start',
}: {
  rx: PrescriptionListItem
  align?: 'start' | 'end'
}) {
  const faxError = rx.latest_fax_error || undefined
  const showFailureNote =
    rx.latest_fax_status === 'failed' && rx.status !== 'failed'

  return (
    <div className={`flex flex-col ${align === 'end' ? 'items-end' : 'items-start'}`}>
      <StatusBadge
        status={rx.status}
        title={rx.status === 'failed' ? faxError : undefined}
      />
      {showFailureNote && (
        <span className="mt-1 text-xs font-medium text-red-600" title={faxError}>
          Delivery failed · attempt {rx.fax_attempt_count}
        </span>
      )}
    </div>
  )
}

/* --------------------------------------------------------------------------
   Shipment Details
   -------------------------------------------------------------------------- */

/**
 * Carrier, tracking number and ship date for a shipped prescription
 * (PORTAL-186). Renders nothing unless the API returned a `shipment`, which it
 * only does for pharmacies with shipment tracking enabled — so the Faxes page
 * is unchanged for every other pharmacy without a frontend flag of its own.
 */
function ShipmentDetails({
  shipment,
  label,
  className = '',
}: {
  shipment: ShipmentInfo | null | undefined
  label?: string
  className?: string
}) {
  if (!shipment?.tracking_number) return null

  const trackingUrl = getTrackingUrl(shipment)
  const details = [
    shipment.carrier,
    shipment.shipping_service,
    shipment.shipped_at ? `Shipped ${formatDate(shipment.shipped_at)}` : null,
  ].filter((part): part is string => !!part)

  return (
    <div className={className}>
      <div>
        {label && <span>{label} </span>}
        {trackingUrl ? (
          <a
            href={trackingUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={`Track on ${shipment.carrier}`}
            className="inline-flex items-center gap-0.5 font-mono whitespace-nowrap text-deep-purple hover:underline"
          >
            {shipment.tracking_number}
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
            <span className="sr-only">(opens in a new tab)</span>
          </a>
        ) : (
          <span className="font-mono whitespace-nowrap">
            {shipment.tracking_number}
          </span>
        )}
      </div>
      {details.length > 0 && (
        // Wrap only between parts, so a narrow column never splits the date.
        <div className="text-xs text-gray-500">
          {details.map((part, i) => (
            <Fragment key={i}>
              {i > 0 && ' · '}
              <span className="whitespace-nowrap">{part}</span>
            </Fragment>
          ))}
        </div>
      )}
    </div>
  )
}

/* --------------------------------------------------------------------------
   View Fax Button
   -------------------------------------------------------------------------- */

function ViewFaxButton({
  rx,
  slug,
}: {
  rx: PrescriptionListItem
  slug: string
}) {
  const [loading, setLoading] = useState(false)

  async function handleDownload() {
    setLoading(true)
    try {
      const url = await getPrescriptionPdfUrl(slug, rx.id)
      if (url) window.open(url, '_blank')
    } catch {
      // Silently fail — admin can retry
    } finally {
      setLoading(false)
    }
  }

  if (!canDownloadPdf(rx)) return null

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleDownload}
      disabled={loading}
      title="View Fax PDF"
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <FileDown className="h-4 w-4" />
      )}
      <span className="ml-1">View Fax</span>
    </Button>
  )
}

/* --------------------------------------------------------------------------
   Retry Fax Button
   -------------------------------------------------------------------------- */

function RetryFaxButton({
  rx,
  onRetried,
}: {
  rx: PrescriptionListItem
  onRetried: () => void
}) {
  const [loading, setLoading] = useState(false)
  const { success, error } = useToast()

  async function handleRetry() {
    setLoading(true)
    try {
      await retryFax(rx.id)
      success(`Fax retry initiated for ${rx.reference_id}`)
      onRetried()
    } catch (err) {
      error(
        err instanceof ApiClientError
          ? err.message
          : 'Failed to retry fax. Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }

  if (!canRetryFax(rx)) return null

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleRetry}
      disabled={loading}
      title="Retry fax delivery"
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <RotateCw className="h-4 w-4" />
      )}
      <span className="ml-1">Retry</span>
    </Button>
  )
}

/* --------------------------------------------------------------------------
   Tracking Number Input
   -------------------------------------------------------------------------- */

/**
 * Manually entered tracking number for a fax (PORTAL-192). Rendered only for
 * pharmacies with `manual_tracking_enabled`; the provider sees the saved value
 * in their History tab. Saving an empty value clears it.
 */
function TrackingNumberInput({
  rx,
  slug,
  onSaved,
}: {
  rx: PrescriptionListItem
  slug: string
  onSaved: (rxId: string, trackingNumber: string | null) => void
}) {
  const saved = rx.manual_tracking_number ?? ''
  const [value, setValue] = useState(saved)
  const [loading, setLoading] = useState(false)
  const { success, error } = useToast()

  // Keep in sync when the list is refetched (filters, paging, retry).
  useEffect(() => {
    setValue(saved)
  }, [saved])

  const dirty = value.trim() !== saved

  async function handleSave() {
    if (!dirty || loading) return
    setLoading(true)
    try {
      const result = await updateTrackingNumber(
        slug,
        rx.id,
        value.trim() || null
      )
      onSaved(rx.id, result.tracking_number)
      success(
        result.tracking_number
          ? `Tracking number saved for ${rx.reference_id}`
          : `Tracking number cleared for ${rx.reference_id}`
      )
    } catch (err) {
      error(
        err instanceof ApiClientError
          ? err.message
          : 'Failed to save tracking number. Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex items-center gap-1">
      <input
        type="text"
        value={value}
        // Read-only while saving: the save resyncs `value` from the response, which
        // would wipe anything typed meanwhile. readOnly (not disabled) keeps focus.
        readOnly={loading}
        maxLength={64}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            handleSave()
          }
        }}
        placeholder="Add tracking #"
        aria-label={`Tracking number for ${rx.reference_id}`}
        className="px-2 py-1 text-sm font-mono border border-gray-300 rounded-md bg-white text-charcoal focus:outline-none focus:ring-2 focus:ring-lavender w-44"
      />
      <Button
        variant="ghost"
        size="sm"
        onClick={handleSave}
        disabled={!dirty || loading}
        title="Save tracking number"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
      </Button>
    </div>
  )
}

/* --------------------------------------------------------------------------
   Loading Skeleton
   -------------------------------------------------------------------------- */

function TableSkeleton() {
  return (
    <div className="animate-pulse space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="h-12 bg-gray-100 rounded" />
      ))}
    </div>
  )
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function Faxes() {
  const { pharmacy } = useAdminAuth()
  const slug = pharmacy?.portal_slug || ''
  // PORTAL-192: an editable tracking-number column for manual-tracking
  // pharmacies (Towncrest), separate from the bot's shipment details.
  const manualTracking = !!pharmacy?.manual_tracking_enabled

  const [data, setData] = useState<PrescriptionListResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [statusFilter, setStatusFilter] = useState('')
  const [providerFilter, setProviderFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [providers, setProviders] = useState<ProviderResponse[]>([])

  const hasActiveFilters =
    !!statusFilter || !!providerFilter || !!dateFrom || !!dateTo || !!search

  const fetchPrescriptions = useCallback(async () => {
    if (!slug) return
    setLoading(true)
    setError(null)

    try {
      const result = await listPrescriptions(slug, {
        status: statusFilter || undefined,
        provider_id: providerFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        search: search || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      })
      setData(result)
    } catch {
      setError('Failed to load faxes. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [slug, page, statusFilter, providerFilter, dateFrom, dateTo, search])

  useEffect(() => {
    fetchPrescriptions()
  }, [fetchPrescriptions])

  // Patch the saved row in place rather than refetching the whole page.
  const handleTrackingSaved = useCallback(
    (rxId: string, trackingNumber: string | null) => {
      setData(
        (prev) =>
          prev && {
            ...prev,
            prescriptions: prev.prescriptions.map((rx) =>
              rx.id === rxId
                ? { ...rx, manual_tracking_number: trackingNumber }
                : rx
            ),
          }
      )
    },
    []
  )

  // Load providers once for the dropdown.
  useEffect(() => {
    if (!slug) return
    let cancelled = false
    listProviders(slug)
      .then((res) => {
        if (!cancelled) setProviders(res.providers)
      })
      .catch(() => {
        // Non-blocking — the rest of the page works without the dropdown.
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0

  function handleStatusChange(value: string) {
    setStatusFilter(value)
    setPage(0)
  }

  function handleProviderChange(value: string) {
    setProviderFilter(value)
    setPage(0)
  }

  function handleDateFromChange(value: string) {
    setDateFrom(value)
    setPage(0)
  }

  function handleDateToChange(value: string) {
    setDateTo(value)
    setPage(0)
  }

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSearch(searchInput.trim())
    setPage(0)
  }

  function handleClearFilters() {
    setStatusFilter('')
    setProviderFilter('')
    setDateFrom('')
    setDateTo('')
    setSearchInput('')
    setSearch('')
    setPage(0)
  }

  return (
    <div className="p-6 sm:p-8">
      <div className="max-w-6xl">
        <div className="mb-6">
          <h1 className="font-serif text-2xl sm:text-3xl text-charcoal mb-2">
            Faxes
          </h1>
          <p className="text-charcoal/70 text-sm">
            Prescription faxes submitted by your providers.
          </p>
        </div>

        {/* Filters */}
        <div className="mb-4 flex flex-wrap items-end gap-3">
          {/* Patient / ref / drug search */}
          <form onSubmit={handleSearchSubmit} className="flex items-end gap-1">
            <div className="flex flex-col">
              <label className="text-xs text-gray-500 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Patient, ref ID, drug, or clinic..."
                  className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-charcoal focus:outline-none focus:ring-2 focus:ring-lavender w-56"
                />
              </div>
            </div>
            <button
              type="submit"
              className="px-3 py-2 text-sm bg-deep-purple text-white rounded-md hover:opacity-90 transition-opacity"
            >
              Go
            </button>
          </form>

          {/* Provider */}
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Provider</label>
            <select
              value={providerFilter}
              onChange={(e) => handleProviderChange(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-charcoal focus:outline-none focus:ring-2 focus:ring-lavender max-w-[220px]"
            >
              <option value="">All Providers</option>
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name?.trim() || p.email}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => handleDateFromChange(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-charcoal focus:outline-none focus:ring-2 focus:ring-lavender"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => handleDateToChange(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-charcoal focus:outline-none focus:ring-2 focus:ring-lavender"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-charcoal focus:outline-none focus:ring-2 focus:ring-lavender"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleClearFilters}
              className="px-3 py-2 text-sm text-charcoal border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors inline-flex items-center gap-1"
            >
              <X className="h-3.5 w-3.5" />
              Clear filters
            </button>
          )}

          <button
            type="button"
            onClick={fetchPrescriptions}
            className="p-2 text-gray-500 hover:text-charcoal transition-colors"
            aria-label="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Content. The skeleton is for the first load only: a refetch (filters,
            paging, retry) keeps the rows mounted so unsaved tracking-number
            drafts in other rows survive it (PORTAL-192). */}
        {loading && !data ? (
          <Card>
            <TableSkeleton />
          </Card>
        ) : error ? (
          <Card>
            <div className="py-8 text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <Button variant="secondary" size="sm" onClick={fetchPrescriptions}>
                Try Again
              </Button>
            </div>
          </Card>
        ) : !data || data.prescriptions.length === 0 ? (
          <Card>
            <div className="py-12 text-center">
              <ClipboardList className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">No faxes found</p>
              <p className="text-sm text-gray-500 mt-1">
                {hasActiveFilters
                  ? 'Try adjusting or clearing your filters.'
                  : 'Submitted prescription faxes will appear here.'}
              </p>
            </div>
          </Card>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block">
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">
                          Date
                        </th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">
                          Ref ID
                        </th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">
                          Provider
                        </th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">
                          Clinic
                        </th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">
                          Patient
                        </th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">
                          Drug Name
                        </th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">
                          Status
                        </th>
                        {manualTracking && (
                          <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">
                            Tracking #
                          </th>
                        )}
                        <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.prescriptions.map((rx) => {
                        const patient = getPatientDisplay(rx)
                        return (
                          <tr
                            key={rx.id}
                            className="hover:bg-gray-50 transition-colors"
                          >
                            <td className="py-3 px-4 text-sm text-charcoal">
                              {formatDate(rx.submitted_at)}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-500 font-mono">
                              {rx.reference_id}
                            </td>
                            <td className="py-3 px-4 text-sm text-charcoal">
                              {getProviderDisplay(rx)}
                            </td>
                            <td className="py-3 px-4 text-sm text-charcoal">
                              {rx.clinic_name || (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-sm text-charcoal">
                              <div>{patient.name}</div>
                              {patient.detail && (
                                <div className="text-xs text-gray-500">
                                  {patient.detail}
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-4 text-sm text-charcoal">
                              {rx.drug_name}
                            </td>
                            <td className="py-3 px-4">
                              <FaxStatusCell rx={rx} />
                              <ShipmentDetails
                                shipment={rx.shipment}
                                className="mt-1 text-xs text-gray-600"
                              />
                            </td>
                            {manualTracking && (
                              <td className="py-3 px-4">
                                <TrackingNumberInput
                                  rx={rx}
                                  slug={slug}
                                  onSaved={handleTrackingSaved}
                                />
                              </td>
                            )}
                            <td className="py-3 px-4 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <ViewFaxButton rx={rx} slug={slug} />
                                <RetryFaxButton
                                  rx={rx}
                                  onRetried={fetchPrescriptions}
                                />
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {data.prescriptions.map((rx) => {
                const patient = getPatientDisplay(rx)
                return (
                  <Card key={rx.id}>
                    <div className="space-y-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm font-medium text-charcoal">
                            {rx.drug_name}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {formatDate(rx.submitted_at)}
                          </p>
                        </div>
                        <FaxStatusCell rx={rx} align="end" />
                      </div>
                      <p className="text-xs text-gray-500 font-mono">
                        {rx.reference_id}
                      </p>
                      <div className="text-sm text-gray-600">
                        <span>Provider: {getProviderDisplay(rx)}</span>
                      </div>
                      {rx.clinic_name && (
                        <div className="text-sm text-gray-600">
                          <span>Clinic: {rx.clinic_name}</span>
                        </div>
                      )}
                      <div className="text-sm text-gray-600">
                        <span>Patient: {patient.name}</span>
                        {patient.detail && (
                          <span className="block text-xs text-gray-500">
                            {patient.detail}
                          </span>
                        )}
                      </div>
                      <ShipmentDetails
                        shipment={rx.shipment}
                        label="Tracking:"
                        className="text-sm text-gray-600"
                      />
                      {manualTracking && (
                        <div className="text-sm text-gray-600">
                          <span className="block mb-1">Tracking #:</span>
                          <TrackingNumberInput
                            rx={rx}
                            slug={slug}
                            onSaved={handleTrackingSaved}
                          />
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <ViewFaxButton rx={rx} slug={slug} />
                        <RetryFaxButton rx={rx} onRetried={fetchPrescriptions} />
                      </div>
                    </div>
                  </Card>
                )
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Showing {page * PAGE_SIZE + 1}–
                  {Math.min((page + 1) * PAGE_SIZE, data.total)} of {data.total}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="p-2 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-600">
                    {page + 1} / {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setPage((p) => Math.min(totalPages - 1, p + 1))
                    }
                    disabled={page >= totalPages - 1}
                    className="p-2 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default Faxes
